/**
 * Günlük ziyaretçi, kaynağa göre (v0.1.216; Sercan, 20 Oct 2026: "gün gün ziyaretleri görelim. grafiklerde tüm takip
 * ettiğimiz kaynaklar ve kaçar ziyaretçi getirdikleri olsun"): GA4 sessions per day, stacked by source group. The five
 * biggest groups get a colour each, the rest fold into "Diğer" (neutral). A legend names every group with its total,
 * and the same numbers are in a table for screen readers.
 *
 * Chart rules of the panel audit (25 Oct 2026): a source keeps its colour whatever its rank (Google is always the
 * first series colour, ChatGPT the second, ...); the takeaway is the chart's first line; the busiest and the last day
 * carry their totals; a tap, a click or the arrow keys pick a day and the readout row under the chart names its
 * sources (the last day until then), so nothing waits behind a hover.
 */

import { SITE_SOURCE_GROUP_LABELS, SITE_SOURCE_GROUPS, type SiteDay, type SiteSourceGroup } from '@twin/contracts';
import { formatIsoDay } from '../lib/format.js';
import { BarValueLabels, ChartFrame, ChartReadout, ChartTakeaway, pickHandlers, useChartPick } from './chart-kit.js';

/** One colour per source on every page; the groups without one fold into "Diğer". */
export const SOURCE_COLOURS: Readonly<Partial<Record<SiteSourceGroup, string>>> = Object.freeze({
  google: 'var(--series-1)',
  chatgpt: 'var(--series-2)',
  etsy: 'var(--series-3)',
  other_ai: 'var(--series-4)',
  outreach: 'var(--series-5)',
  direct: 'var(--data-neutral)',
});
const OTHER = 'rest';
const OTHER_COLOUR = 'var(--ink-3)';

type Slot = { key: SiteSourceGroup | typeof OTHER; label: string; colour: string; total: number };

function n(value: number): string {
  return value.toLocaleString('tr-TR');
}

/** The groups to draw, biggest first: every source with its own colour, then the rest as one neutral slot. */
export function sourceSlots(daily: readonly SiteDay[]): Slot[] {
  const totals = new Map<SiteSourceGroup, number>();
  for (const day of daily) {
    for (const group of SITE_SOURCE_GROUPS)
      totals.set(group, (totals.get(group) ?? 0) + (day.sourceSessions[group] ?? 0));
  }
  const ranked = SITE_SOURCE_GROUPS.filter((group) => (totals.get(group) ?? 0) > 0).sort(
    (a, b) =>
      (totals.get(b) ?? 0) - (totals.get(a) ?? 0) || SITE_SOURCE_GROUPS.indexOf(a) - SITE_SOURCE_GROUPS.indexOf(b),
  );
  const shown = ranked.filter((group) => SOURCE_COLOURS[group] !== undefined);
  const slots: Slot[] = shown.map((group) => ({
    key: group,
    label: SITE_SOURCE_GROUP_LABELS[group],
    colour: SOURCE_COLOURS[group] ?? OTHER_COLOUR,
    total: totals.get(group) ?? 0,
  }));
  const folded = ranked.filter((group) => SOURCE_COLOURS[group] === undefined);
  if (folded.length > 0) {
    slots.push({
      key: OTHER,
      label: `Diğer (${folded.map((group) => SITE_SOURCE_GROUP_LABELS[group]).join(', ')})`,
      colour: OTHER_COLOUR,
      total: folded.reduce((sum, group) => sum + (totals.get(group) ?? 0), 0),
    });
  }
  return slots;
}

function amountOf(day: SiteDay, slot: Slot, slots: readonly Slot[]): number {
  if (slot.key !== OTHER) return day.sourceSessions[slot.key] ?? 0;
  const named = new Set(slots.map((item) => item.key));
  return SITE_SOURCE_GROUPS.filter((group) => !named.has(group)).reduce(
    (sum, group) => sum + (day.sourceSessions[group] ?? 0),
    0,
  );
}

/** "Google 12 · ChatGPT 3": a day's sources in words. */
function dayWords(day: SiteDay, slots: readonly Slot[]): string {
  return (
    slots
      .map((slot) => ({ slot, value: amountOf(day, slot, slots) }))
      .filter((item) => item.value > 0)
      .map((item) => `${item.slot.label.replace(/ \(.*\)$/, '')} ${n(item.value)}`)
      .join(' · ') || 'oturum yok'
  );
}

/**
 * The picked day in full under a chart (Sercan, 27 Oct 2026: "grafiklerde günlere tıklayınca grafiğin altında özetler
 * yazsın"): GA4 visits by source, the server log's entries, page views, AI entries and bot requests, and Google's clicks
 * and impressions. A number a source has not given for that day reads "-" (GA4 and Search Console come a few days late).
 */
export function DaySummary({ day }: { day: SiteDay | undefined }) {
  if (day === undefined) return null;
  const slots = sourceSlots([day]);
  const value = (v: number | null | undefined) => (v === null || v === undefined ? '-' : n(v));
  const rows: [string, string, string | null][] = [
    ['Ziyaret (GA4)', value(day.sessions), day.sessions === null || slots.length === 0 ? null : dayWords(day, slots)],
    ['Dışarıdan giriş', value(day.entries), day.aiEntries > 0 ? `yapay zekâdan ${n(day.aiEntries)}` : null],
    ['Sayfa görüntüleme', value(day.pageViews), null],
    ['Bot istekleri', value(day.crawlerHits), null],
    [
      'Google',
      day.googleImpressions === null ? '-' : `${value(day.googleClicks)} tık`,
      day.googleImpressions === null ? 'henüz gelmedi' : `${n(day.googleImpressions)} gösterim`,
    ],
  ];
  return (
    <section className="sx-day" aria-label={`${formatIsoDay(day.day)} özeti`}>
      <h4 className="sx-day__title">{formatIsoDay(day.day)}</h4>
      <dl className="sx-day__nums">
        {rows.map(([label, main, sub]) => (
          <div key={label} className="sx-day__num">
            <dt>{label}</dt>
            <dd className="mono">{main}</dd>
            {sub === null ? null : <dd className="sx-day__sub">{sub}</dd>}
          </div>
        ))}
      </dl>
    </section>
  );
}

export function SourceDailyChart({ daily }: { daily: readonly SiteDay[] }) {
  const days = daily.filter((day) => day.sessions !== null);
  const slots = sourceSlots(days);
  const pick = useChartPick(days.length);
  if (days.length === 0 || slots.length === 0) {
    return <p className="sx-empty">Google Analytics'te henüz kaynak bilgisi yok.</p>;
  }
  const totals = days.map((day) => slots.reduce((sum, slot) => sum + amountOf(day, slot, slots), 0));
  const max = Math.max(1, ...totals);
  const width = 600;
  const height = 160;
  const gap = 2;
  const bar = (width - gap * (days.length - 1)) / days.length;
  const all = totals.reduce((sum, value) => sum + value, 0);
  const peakIndex = totals.indexOf(Math.max(...totals));
  const titleId = 'sx-src-title';
  const title = `Günlük ziyaretçi, kaynağa göre: ${formatIsoDay(days[0]?.day ?? '')} ile ${formatIsoDay(days.at(-1)?.day ?? '')} arası ${n(all)} oturum; en yoğun gün ${formatIsoDay(days[peakIndex]?.day ?? '')} (${n(totals[peakIndex] ?? 0)}). ${slots.map((slot) => `${slot.label} ${n(slot.total)}`).join(', ')}.`;
  const lead = slots[0];
  const shown = days[pick.index];
  const readout =
    shown === undefined
      ? ''
      : `${formatIsoDay(shown.day)}: ${n(totals[pick.index] ?? 0)} oturum · ${dayWords(shown, slots)}`;
  return (
    <figure className="sx-src">
      <ChartTakeaway>
        {n(all)} oturum; en çok {lead?.label.replace(/ \(.*\)$/, '')} getirdi ({n(lead?.total ?? 0)})
      </ChartTakeaway>
      <ChartFrame label="Günlük ziyaretçi: gün seç" count={days.length} pick={pick} valueText={readout}>
        <div className="sx-src__plot">
          <svg
            className="sx-src__svg"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>{title}</title>
            {[0.5, 1].map((share) => (
              <line
                key={share}
                className="sx-src__grid"
                x1={0}
                x2={width}
                y1={height - 1 - share * (height - 4)}
                y2={height - 1 - share * (height - 4)}
              />
            ))}
            <line className="au-bars__base" x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} />
            {days.map((day, index) => {
              const x = index * (bar + gap);
              let top = height - 1;
              return (
                <g key={day.day} className={pick.index === index ? 'is-focus' : undefined}>
                  <rect
                    className="sx-src__hit"
                    x={x}
                    y={0}
                    width={bar + gap}
                    height={height}
                    {...pickHandlers(pick, index)}
                  />
                  {slots.map((slot) => {
                    const value = amountOf(day, slot, slots);
                    if (value === 0) return null;
                    const h = (value / max) * (height - 4);
                    top -= h;
                    // A 1 px surface gap between stacked segments keeps neighbours apart.
                    return (
                      <rect
                        key={slot.key}
                        x={x}
                        y={top + 0.5}
                        width={bar}
                        height={Math.max(0.5, h - 1)}
                        fill={slot.colour}
                        pointerEvents="none"
                      />
                    );
                  })}
                </g>
              );
            })}
          </svg>
          <BarValueLabels values={totals} max={max} text={n} />
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <DaySummary day={shown} />
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{formatIsoDay(days[0]?.day ?? '')}</span>
        <span>{formatIsoDay(days.at(-1)?.day ?? '')}</span>
      </figcaption>
      <ul className="sx-src__legend" aria-label="Kaynaklar ve getirdikleri oturum">
        {slots.map((slot) => (
          <li key={slot.key}>
            <span className="sx-src__swatch" style={{ background: slot.colour }} aria-hidden="true" />
            <span>{slot.label}</span>
            <span className="mono">{n(slot.total)}</span>
          </li>
        ))}
      </ul>
      {/* A table ignores the 1 px width of .sr-only; the wrapper clips it so the page cannot scroll sideways. */}
      <div className="sr-only">
        <table>
          <caption>Günlük oturum, kaynağa göre</caption>
          <thead>
            <tr>
              <th scope="col">Gün</th>
              {slots.map((slot) => (
                <th key={slot.key} scope="col">
                  {slot.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.day}>
                <th scope="row">{formatIsoDay(day.day)}</th>
                {slots.map((slot) => (
                  <td key={slot.key}>{amountOf(day, slot, slots)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
